import moment from 'moment';
import { isNil, pathOr } from 'ramda';
import { get } from 'lodash';

// If this has more than 2 elements, then refactor shouldCalculateMORDSubsidy
const NO_PRORATE_SUBSIDY_CODES = ['SUBRENT', 'SUBRENTCRED'];

export function getMoveInProratedTransactions(
  monthlyTransactions: Array<Object>,
  startDate: any,
  moveInApplyFullAmountToProRate: boolean,
  checkForCurrentMonth?: boolean = false,
  roundProratedRents: boolean,
  correctProrateOnMoveinScreenFlag: boolean,
): Array<Object> {
  if (!startDate) {
    return [];
  }
  const startsOnMoveInMonth = (transactionStart: any) =>
    moment(transactionStart).isBefore(moment(startDate).endOf('month'));
  const startDateOnCurrentMonth =
    moment(startDate).isSameOrAfter(moment().startOf('month')) &&
    moment(startDate).isSameOrBefore(moment().endOf('month'));
  /**
   * When checking for current month (Commercial)
   * If the start date is before the start of current month, do not generate prorates
   * */
  if (checkForCurrentMonth && !startDateOnCurrentMonth) {
    return [];
  }
  // Function to check if a monthly transaction is no longer in use (same start/end date)
  const isDeleted = (transactionStart: any, transactionEnd: any, amt) =>
    !isNil(transactionEnd) &&
    moment(transactionStart).isSame(moment(transactionEnd));

  // Function to check if the monthly transaction already ended
  const isNotPast = (transactionEnd: any) =>
    isNil(transactionEnd) || moment(transactionEnd).isAfter(moment(startDate));

  const hasProrateCode = (transaction: any) =>
    transaction.propertyTransactionCode.proRate ?? false;
  if (!isNil(startDate) && !isNil(monthlyTransactions)) {
    // Filter all the monthly transactions that are eligible to prorate
    const applicableTransactions = monthlyTransactions.filter(
      (mt) =>
        (startsOnMoveInMonth(mt.startDate) ||
          transactionBecameActiveAfterStartMonth(mt.startDate, startDate)) &&
        !isDeleted(mt.startDate, mt.endDate, mt.amount) &&
        isNotPast(mt.endDate),
    );

    return applicableTransactions.map((monthlyTransaction: Object) => {
      const anyTxActivePostStartDate = applicableTransactions.some(
        (applicableTx) =>
          transactionBecameActiveAfterStartMonth(
            applicableTx.startDate,
            startDate,
          ),
      );
      const activePostStartDate = transactionBecameActiveAfterStartMonth(
        monthlyTransaction.startDate,
        startDate,
      );
      const { amount, propertyTransactionCodeId } = monthlyTransaction;
      const transactionHasProrateCode = hasProrateCode(monthlyTransaction);

      const startMonthProratedAmount = moveInApplyFullAmountToProRate
        ? amount
        : getRemainingMonthProratedAmount(
            startDate,
            amount,
            activePostStartDate,
            transactionHasProrateCode,
          );

      const elapsedFullMonthsAmount = getElapsedFullMonthsProratedAmount(
        activePostStartDate ? monthlyTransaction.startDate : startDate,
        amount,
        monthlyTransaction.endDate,
        activePostStartDate,
      );
      let proratedAmount = 0;
      if (!correctProrateOnMoveinScreenFlag) {
        proratedAmount = Number(
          elapsedFullMonthsAmount + startMonthProratedAmount,
        ).toFixed(2);
      } else {
        if (moment(startDate).isSameOrAfter(moment())) {
          proratedAmount = Number(startMonthProratedAmount).toFixed(2);
        } else if (moment(startDate).isBefore(moment())) {
          proratedAmount = Number(
            elapsedFullMonthsAmount + startMonthProratedAmount,
          ).toFixed(2);
        }
      }

      const {
        code,
        description,
        transactionType: { name },
      } = monthlyTransaction?.propertyTransactionCode?.transactionCode ?? {
        code: '',
        description: '',
        transactionType: { name: '' },
      };

      const txDate = anyTxActivePostStartDate
        ? {
            date: activePostStartDate
              ? moment(monthlyTransaction.startDate).startOf('month')
              : moment(startDate),
          }
        : {};

      return {
        proratedAmount: roundProratedRents
          ? Math.round(proratedAmount)
          : proratedAmount,
        amount,
        propertyTransactionCodeId,
        code,
        description,
        name,
        ...txDate,
      };
    });
  }
  return [];
}

export function getMoveOutProratedTransactions(
  monthlyTransactions: Array<Object>,
  moveOutDate: any,
  moveInApplyFullAmountToProRate: boolean,
  roundProratedRents: boolean,
  processRDSubsidyRents: boolean,
) {
  const monthlyProRatedTransactions = monthlyTransactions
    ? monthlyTransactions.reduce((prev, monthlyTransaction) => {
        const isNotStartOfMonthMoveOutProratedTransaction =
          !monthlyTransaction.isMoveOutProrate;
        const startDate = moment(monthlyTransaction.startDate);
        const firstDayOfMoveOutMonth = moment(moveOutDate).startOf('month');
        const startDateIsOnOrBeforeFirstDayOfMoveOutMonth =
          startDate.isSameOrBefore(firstDayOfMoveOutMonth);
        const endDateIsOnOrAfterFirstDayOfMoveOutMonthOrNull =
          moment(monthlyTransaction.endDate).isSameOrAfter(
            firstDayOfMoveOutMonth,
          ) || !monthlyTransaction.endDate;

        if (
          startDateIsOnOrBeforeFirstDayOfMoveOutMonth &&
          endDateIsOnOrAfterFirstDayOfMoveOutMonthOrNull &&
          isNotStartOfMonthMoveOutProratedTransaction
        ) {
          const hasProRated =
            monthlyTransaction.propertyTransactionCode.proRate;
          if (!hasProRated) {
            return prev;
          }
          const id = monthlyTransaction.id;
          const amount = pathOr(0, ['amount'], monthlyTransaction);
          const code = pathOr(
            '',
            ['propertyTransactionCode', 'proRate', 'transactionCode', 'code'],
            monthlyTransaction,
          );
          const description = pathOr(
            '',
            [
              'propertyTransactionCode',
              'proRate',
              'transactionCode',
              'description',
            ],
            monthlyTransaction,
          );

          const name = pathOr(
            '',
            [
              'propertyTransactionCode',
              'proRate',
              'transactionCode',
              'tt',
              'name',
            ],
            monthlyTransaction,
          );
          const propertyTransactionCodeId = pathOr(
            '',
            ['propertyTransactionCode', 'proRatePropertyTransactionCodeId'],
            monthlyTransaction,
          );
          const proratedAmount = getRemainingMonthProratedCredAmount(
            moveOutDate,
            amount,
          );
          const priorMonthsCreditAmount = getElapsedFullMonthsProratedAmount(
            moveOutDate,
            amount,
          );

          const finalProratedAmount = (() => {
            if (
              processRDSubsidyRents &&
              shouldCalculateMORDSubsidy(code, monthlyTransactions)
            ) {
              return 0;
            }

            const fullProrateAmount = proratedAmount + priorMonthsCreditAmount;
            const toFixedProratedAmount = Number(fullProrateAmount.toFixed(2));
            return roundProratedRents
              ? Math.round(toFixedProratedAmount)
              : toFixedProratedAmount;
          })();
          prev.push({
            proratedAmount: finalProratedAmount,
            monthlyTransactionId: id,
            amount,
            code,
            description,
            name,
            propertyTransactionCodeId,
          });
        }
        return prev;
      }, [])
    : [];
  return monthlyProRatedTransactions;
}

export function getRemainingMonthProratedCredAmount(moveOutDate, amount) {
  // Compute the prorated amount to credit based on the number
  // of calendar days that are missing of the move out month.
  const daysInMonth = moment(moveOutDate).daysInMonth();
  const lastDayOfMoveOutMonth = moment(moveOutDate).add(1, 'months').date(0);
  return (
    parseFloat(amount) *
    (lastDayOfMoveOutMonth.diff(moveOutDate, 'days') / daysInMonth)
  );
}

export function getElapsedMonthProratedAmount(moveOutDate, amount) {
  // Compute the prorated amount to charge based on the number
  // of calendar days that have elapsed from the move out month.
  const moveOutDateFormatted = moment(moveOutDate);
  const daysInMonth = moment(moveOutDate).daysInMonth();
  const daysDiff = moveOutDateFormatted.date();
  return (amount / daysInMonth) * daysDiff;
}

export function getRemainingMonthProratedAmount(
  moveInDate,
  amount: number,
  activePostStartDate = false, // became active after the move in month
  hasProrateCode = true,
) {
  if (activePostStartDate) {
    // only prorates for the remaining months
    return 0;
  }

  // if txn doesn't have a prorate code, return full amount
  if (!hasProrateCode) {
    return amount;
  }

  // Compute the prorated amount to pay based on the number
  // of calendar days that remain of the move in month.
  const moveInDateFormatted = moment(moveInDate);
  const daysInMonth = moment(moveInDate).daysInMonth();
  const daysDiff = daysInMonth - moveInDateFormatted.date() + 1;
  return (daysDiff * amount) / daysInMonth;
}

function lookUpTransactionByCode(
  transactions: Array<Object>,
  code: string,
): Object {
  return transactions.find(
    (transaction) =>
      get(transaction, 'propertyTransactionCode.transactionCode.code', null) ===
      code,
  );
}

function shouldCalculateMORDSubsidy(
  currentCode: string,
  transactions: Array<Object>,
): boolean {
  if (!NO_PRORATE_SUBSIDY_CODES.includes(currentCode)) {
    return false;
  }

  const otherRDSubsidyCode =
    NO_PRORATE_SUBSIDY_CODES[0] === currentCode
      ? NO_PRORATE_SUBSIDY_CODES[1]
      : NO_PRORATE_SUBSIDY_CODES[0];

  return Boolean(lookUpTransactionByCode(transactions, otherRDSubsidyCode));
}

function getElapsedFullMonthsProratedAmount(
  prorateDate,
  amount: number,
  endDate,
  activePostStartDate = false,
) {
  // Compute how many months have passed from the given date
  // to the current date (or transaction end date),
  // and return how many "first of month" charges should be added.
  const proratedDateFormatted = moment(prorateDate).startOf('month');
  let monthsDifference = 0;
  const transactionEndDate = getTxnEndDateForElapsedFullMonthsProratedAmount(
    prorateDate,
    endDate,
    activePostStartDate,
  );
  if (activePostStartDate) {
    monthsDifference = transactionEndDate
      .clone()
      .endOf('month')
      .diff(proratedDateFormatted, 'months', true);
  } else {
    monthsDifference = transactionEndDate
      .clone()
      .startOf('month')
      .diff(proratedDateFormatted, 'months', true);
  }
  const monthsDiff = Math.ceil(monthsDifference);
  return monthsDiff * amount;
}

const transactionBecameActiveAfterStartMonth = (
  transactionStart: any,
  startDate,
) => {
  const transactionStartMoment = moment(transactionStart);
  const transactionStartsAfterStartMonth = transactionStartMoment
    .clone()
    .isAfter(moment(startDate).endOf('month'));
  const transactionStartsOnTheFirstOfItsStartDateMonth =
    transactionStartMoment.isSame(
      transactionStartMoment.clone().startOf('month'),
      'day',
    );
  const isRetroactiveMoveIn = moment(startDate).isBefore(
    moment().startOf('month'),
  );
  const transactionIsActive = moment(transactionStart).isSameOrBefore(
    moment(),
    'day',
  );
  return (
    isRetroactiveMoveIn &&
    transactionIsActive &&
    transactionStartsAfterStartMonth &&
    transactionStartsOnTheFirstOfItsStartDateMonth
  );
};

const getTxnEndDateForElapsedFullMonthsProratedAmount = (
  prorateDate,
  transactionEndDate,
  activePostStartDate,
) => {
  const today = moment();
  const transactionEndsAfterThisMonth = moment(transactionEndDate).isAfter(
    today,
    'month',
  );
  if (activePostStartDate) {
    // if the txn became active after the start date,
    // we use the end date of the transaction (if it's not in a future month [last day of current month otherwise])
    // or the current date if no end date is provided
    const dateToUse = transactionEndsAfterThisMonth
      ? today.clone().endOf('month').format('YYYY-MM-DD')
      : transactionEndDate;
    return moment(dateToUse).isValid() ? moment(dateToUse) : moment();
  } else {
    // otherwise,
    // we check if the transaction ends after this month
    // if it does, we use the last day of the month of the prorate date (the MLT job will handle future months)
    // otherwise, we use the transaction end date if provided or the current date
    let dateToUse = '';
    if (transactionEndsAfterThisMonth) {
      dateToUse = moment(prorateDate).endOf('month').format('YYYY-MM-DD');
    } else {
      dateToUse = transactionEndDate
        ? transactionEndDate
        : today.clone().format('YYYY-MM-DD');
    }
    return moment(dateToUse);
  }
};

const setProratedAmount = ({ startDate, amount, updateFn }) => {
  if (startDate !== '' && !isNil(startDate)) {
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) {
      updateFn('0.00');
      return;
    }
    const proratedAmount = (
      getRemainingMonthProratedAmount(startDate, parsedAmount) || 0
    ).toFixed(2);

    updateFn(proratedAmount);
  }
};

export const setProratedRentAmount = ({ startDate, rentAmount, updateFn }) =>
  setProratedAmount({ startDate, amount: rentAmount, updateFn });

export const setProratedOtherAmount = ({ startDate, otherAmount, updateFn }) =>
  setProratedAmount({ startDate, amount: otherAmount, updateFn });
